import {
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { TOKENS, UserFlowService } from '@trustedshops/tswp-core-masterdata';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
} from 'rxjs';
import { Company } from '../../models/company.interface';
import { CompanyService } from '../../services/company.service';
import { UserService } from '../../services/user.service';
import {
  TOKENS as TRACKING_TOKENS,
  TrackingService,
} from '@trustedshops/tswp-core-analytics';
import { ChannelsService } from '../../services/channels.service';
import { LogService, TOKENS as COMMON_TOKENS } from '@trustedshops/tswp-core-common';

@Component({
  standalone: false,
  encapsulation: ViewEncapsulation.None,
  selector: 'app-upgrade-center-cta',
  templateUrl: 'upgrade-center-cta.component.html',
})
export class UpgradeCenterCtaComponent implements OnInit, OnDestroy {
  private static readonly TYPE = 'UpgradeCenterCtaComponent';

  @Input({ required: true }) onboardingProgress$: Observable<number>;
  buttonStyle: 'primary' | 'outline' = 'outline';
  isVisible$: Observable<boolean>;

  #progressMonitorSubscription: Subscription;
  #isAllowedTargetMarket$: Observable<boolean>;
  readonly #allowedTargetMarkets: string[] = ['DEU', 'AT', 'CH'];
  readonly #upgradeCenterGAEvent = 'UPGRADE_CENTER_CTA_CLICKED';

  constructor(
    private readonly router: Router,
    @Inject(TOKENS.UserFlowService)
    private readonly _userflowService: UserFlowService,
    @Inject(TRACKING_TOKENS.TrackingService)
    private readonly _trackingService: TrackingService,
    private readonly _channelService: ChannelsService,
    private readonly _userService: UserService,
    private readonly _companyService: CompanyService,
    @Inject(COMMON_TOKENS.LogService)
    private readonly _logService: LogService
  ) { }

  public ngOnInit(): void {
    this._evaluateAllowedTargetMarkets();
    this._evaluateCTAVisibility();
    this._startOnboardingProgressListener();
    this._startUserflowChecklistDismissalListener();
  }

  public redirectToUpgradeCenter(): void {
    this.router
      .navigate(['/upgrade-center'])
      .then(this._triggerGAEvent.bind(this));
  }

  private _triggerGAEvent(): void {
    this._trackingService.trackEvent({
      eventType: 'event',
      eventParameters: [
        this.#upgradeCenterGAEvent,
        {
          event_type: 'External_Navigation_Event',
          event_category: 'upgrade_center',
        },
      ],
    });
  }

  /**
   * changes button style to primary when checklist is dismissed
   */
  private _startUserflowChecklistDismissalListener(): void {
    this._userflowService.userflow.on('checklistEnded', () => {
      this.buttonStyle = 'primary';
    });
  }

  /**
   * changes button style to primary when onboarding is completed
   */
  private _startOnboardingProgressListener(): void {
    this.#progressMonitorSubscription = this.onboardingProgress$
      .pipe(filter((progress) => !!progress))
      .subscribe((progress) => {
        this.buttonStyle = progress === 100 ? 'primary' : 'outline';
      });
  }

  /**
   * evaluates target market of the selected channel to determine if it is allowed for upgrade
   */
  private _evaluateAllowedTargetMarkets(): void {
    this.#isAllowedTargetMarket$ =
      this._channelService.selectedChannelSEOSettings$.pipe(
        filter((seoSettings) => !!seoSettings),
        map((seoSettings) =>
          this.#allowedTargetMarkets.includes(seoSettings?.targetMarket)
        ),
        distinctUntilChanged(),
        catchError((error) => {
          this._logService.error(
            UpgradeCenterCtaComponent.TYPE,
            new Error(`Failed to evaluate allowed target markets: ${JSON.stringify(error)}`)
          );
          return of(false);
        })
      );
  }

  /**
   * determines visibility of the upgrade-center CTA based on company's eligibility for an upgrade
   */
  private _evaluateCTAVisibility(): void {
    this.isVisible$ = this.#isAllowedTargetMarket$.pipe(
      filter((isTargetMarketAllowed) => isTargetMarketAllowed),
      switchMap(() => this._obtainAccountRef$()),
      switchMap((accountRef) => this._evaluateCompanyEligibility$(accountRef)),
      distinctUntilChanged(),
      catchError((error) => {
        this._logService.error(
          UpgradeCenterCtaComponent.TYPE,
          new Error(`Failed to evaluate CTA visibility: ${JSON.stringify(error)}`)
        );
        return of(false);
      })
    );
  }

  private _obtainAccountRef$(): Observable<string> {
    return this._userService.user$.pipe(
      filter((user) => !!user),
      filter((user) => !!user['https://etrusted.com']),
      map((user) => user['https://etrusted.com'].accountRef),
      filter((accountRef) => !!accountRef),
      distinctUntilChanged(),
      catchError((error) => {
        this._logService.error(
          UpgradeCenterCtaComponent.TYPE,
          new Error(`Failed to obtain account reference: ${JSON.stringify(error)}`)
        );
        return EMPTY;
      })
    );
  }

  private _evaluateCompanyEligibility$(companyId: string): Observable<boolean> {
    return this._companyService
      .getCompany(companyId)
      .pipe(map((c) => this._isEligibleForUpgrade(c)),
        catchError((error) => {
          this._logService.error(
            UpgradeCenterCtaComponent.TYPE,
            new Error(`Failed to evaluate company eligibility for company ${companyId}: ${JSON.stringify(error)}`)
          );
          return of(false);
        }));
  }

  private _isEligibleForUpgrade(company: Company): boolean {
    const { contractStatus, contractType, currency, turnover } = company;
    // eligibility criteria derived from business-logic AC:CC-835
    return (
      contractStatus === 'ACTIVE' &&
      contractType === 'PAID' &&
      turnover <= 5000000 &&
      (currency === 'EUR' || currency === 'CHF')
    );
  }

  public ngOnDestroy(): void {
    this.#progressMonitorSubscription?.unsubscribe();
  }
}
